import { SelectChangeEvent } from '@mui/material'
import { differenceInMinutes } from 'date-fns'
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { 
  reservationStatus, 
  reservableUnitPatterMinutes, 
  yesNo, 
  purposeOfUse as purposeOfUseValue, 
  useReasonCode as useReasonCodeValue, 
  Flag, availabilityStatus, 
  lotteryAcceptTypeOption, 
  useReasonDisplayTypes
} from '../../containers/common/constant/classification'
import {
  FacilityReserveEntry,
  selectFacilityReserveEntry,
  setFacilityReserve,
  setReserveModifyUpdateDatetime,
  IsCertificateUseReasonViewFlags,
  clearFacilityReserve,
} from '../../containers/common/store/slices/facilityReserve'
import { executeGetChildren } from '../../dataAccess/webApi/dao/childrenDao'
import { executeGetFacilityReservationsDays } from '../../dataAccess/webApi/dao/facilityReservationsDaysDao'
import { executeGetFacilityReservationsWeekCitizen } from '../../dataAccess/webApi/dao/facilityReservationsWeekCitizenDao'
import { GetFacilityDto } from '../../dataAccess/webApi/dto/facilitiesDto'
import { GetFacilityReservationsDaysDto } from '../../dataAccess/webApi/dto/facilityReservationsDaysDto'
import { GetFacilityReservationsWeekCitizenDto } from '../../dataAccess/webApi/dto/facilityReservationsWeekCitizenDto'
import { GetReservationsDto } from '../../dataAccess/webApi/dto/reservationsDto'
import { translate } from '../../i18n'
import { DateRange, ElapsedMillisecond, getNowTrimedTime, isValidDate, toApiYmd, formatHyphenYmd, formatYmdHm } from '../../utils/dateUtil'
import { promiseAllConcurrency } from '../../utils/promiseUtil'
import { fromNumber } from '../../utils/stringUtil'
import { castNonNullable, NullPropsToUndefinedType } from '../../utils/typeUtil'
import { facilityLotteryReservationFormUrl, facilityReservationFormUrl } from '../common/constant/appUrl'
import { useErrorHandle } from '../common/error/errorHandler'
import { getFacility } from '../common/facility'
import { getReservation, Reservation, ChildReservationInfoType, DiagonosisType, isUseReasonCategoryHidden} from '../common/reservation'
import { notifyMessage, showLoading } from '../common/store/slices/application'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { executeGetIsolationFlag, executeGetReservations } from '../../dataAccess/webApi/dao/reservationsDao'
import { getUseReason } from '../common/useReason'
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto'
import { clearLotteryReserve, LotteryReserveEntry, selectLotteryReserveEntry, setLotteryReserve } from '../common/store/slices/lotteryReserve'
import { ChildLotteryReservationInfoType, LotteryReservation } from '../common/lotteryReservation'
import { getUsageGuidStr } from '../common/reservationSelection';
import { UseReasonOptions } from '../../views/components/facilityReservationForm/useReasonRadioGroup'
import { getDecodeMstValue } from '../common/getDecodeMstValue'
import { DecodeMstCategory } from '../common/constant/decodeMst'

interface InsertUrlParams {
  facilityId: string
  childId: string
}
interface UpdateUrlParams {
  reservationNo: string
}
type UrlParams = InsertUrlParams | UpdateUrlParams

type DesiredDatetime = {
  value: {
    baseDateOfTime: Date // 時間の基準となる日付
    range: DateRange // 日時範囲
    status: string // 空き状況ステータス
  }
}
type DetailDescription = {
  value: string | null
}
export interface Inputs {
  // 利用希望日時
  usageDesiredDatetimes: DesiredDatetime[]
  purposeOfUse: string | null
  useReasonDetailIrregular: string | null
  useReasonDetailEmg: string | null
  useReasonDetailRefresh: string | null
  useReasonDetailDescriptionsIrregular: DetailDescription[]
  useReasonDetailDescriptionsEmg: DetailDescription[]
  useReasonDetailDescriptionsRefresh: DetailDescription[]
  selectedChildIds: string[]
  diagonosis: DiagonosisType | undefined
}

export type Child = { value: string; label: string }
export interface LocationState {
  isKeep?: boolean // 入力済み情報から復元を試みる場合true
  activeDate: ElapsedMillisecond
  children: Child[]
}

export const getIsolationFlag = async (facilityId: string, usageDate: string): Promise<Boolean> => {
  const inputData = { 
    facilityId,
    usageDate
  }
  return (await executeGetIsolationFlag(inputData)).result
}

export const getDiagonosisDecodeMstValue = async () => {
  const diagonosisDecodeMst = await getDecodeMstValue({
    diagonosis: DecodeMstCategory.DIAGNOSIS,
  })
  const diagonosisMst = diagonosisDecodeMst.diagonosis.map((diagonosis) => ({value: diagonosis.code, label: diagonosis.name}))
  return diagonosisMst
}

type CommonFlag = typeof yesNo[keyof typeof yesNo]
export interface PageState {
  prevReservation?: Reservation // 変更前利用予約情報
  activeDate: Date
  selectedChildIds: string[] // 選択されたお子様IDリスト
  children: Child[] // 選択対象お子さまリスト
  facilityId: string
  facility: NullPropsToUndefinedType<GetFacilityDto> // 施設情報
  purposeOfUse?: string | null
  useReasonDetailCategory: string | null
  useReasonDetailDescription: string | null
  commonFlag: CommonFlag
  useReasonOptions: UseReasonOptions
  isCertificateUseReasonViewFlags: IsCertificateUseReasonViewFlags
  usageGuideStr: string
  useReason: NullPropsToUndefinedType<GetUseReasonDto> // 利用目的マスタ情報
  availabilities: GetFacilityReservationsWeekCitizenDto[]
  initialValidationAvailabilities: GetFacilityReservationsDaysDto[] // 入力済み情報から復元する際に空き状況を再度確認する用
  diagonosisDecodeMst: { value: string; label: string }[]
  isolationFlag: Boolean | null
  diagonosis: {
    first: string | null,
    second: string | null
  },
  acceptLottery: boolean,
  note: string | null,
  lotteryNote: string | null
}

interface ShowMessage {
  Date: Date;
  message: string;
}

// 施設情報同時取得最大値
const MAX_WORKER_COUNT = 4

// facilityIdがパラメータにある場合＝新規予約、ない場合＝予約変更
const isInsertParams = (urlParams: UrlParams): urlParams is InsertUrlParams => {
  return 'facilityId' in urlParams
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory<LocationState | undefined>()
  const urlParams = useParams<UrlParams>()
  const { addOperationLog, attachAccessData } = useOperationLog()
  const locationState = history.location.state

  const facilityReserveEntry = useSelector(selectFacilityReserveEntry)
  const lotteryReserveEntry = useSelector(selectLotteryReserveEntry)
  const reserveEntry: FacilityReserveEntry | LotteryReserveEntry | undefined = facilityReserveEntry ?? lotteryReserveEntry
  const sysCtrl = useSelector(selectSystemControl)

  const initialized = useRef(false);

  // ページ情報
  const [state, setState] = useState<PageState>()
  
  // 変更前の日時
  const previousRange = useMemo(() => {
    if (state?.prevReservation) {
      return {
        range: {
          from: new Date(state.prevReservation.useFromDatetime),
          to: new Date(state.prevReservation.useToDatetime),
        },
        baseDateOfTime: new Date(state.prevReservation.usageDate),
        isWaiting: state.prevReservation.status === reservationStatus.wait,
      }
    }
  }, [state?.prevReservation])

  // 「予約期間外」の文言表示
  const notifyDateFetchErrorMessage = useCallback(() => dispatch(notifyMessage(translate('facilityReservationSelection.error.noReservationReceptionSetting'))),[])

  // フォーム関連
  const formMethods = useForm<Inputs>()
  const purposeOfUse = useWatch({ name: 'purposeOfUse', control: formMethods.control})
  const useReasonDetailIrregular = useWatch({ name: 'useReasonDetailIrregular', control: formMethods.control })
  const useReasonDetailEmg = useWatch({ name: 'useReasonDetailEmg', control: formMethods.control })
  const useReasonDetailRefresh = useWatch({ name: 'useReasonDetailRefresh', control: formMethods.control })
  const { append: appendUseReasonDetailDescriptionsIrregular } = useFieldArray({
    name: 'useReasonDetailDescriptionsIrregular',
    control: formMethods.control,
  })
  const { append: appendUseReasonDetailDescriptionsEmg } = useFieldArray({
    name: 'useReasonDetailDescriptionsEmg',
    control: formMethods.control,
  })
  const { append: appendUseReasonDetailDescriptionsRefresh } = useFieldArray({
    name: 'useReasonDetailDescriptionsRefresh',
    control: formMethods.control,
  })

  const getNotes = useCallback((
    facility: NullPropsToUndefinedType<GetFacilityDto>, 
    acceptLottery: boolean, 
    purposeOfUse: string | null | undefined,
    commonFlag: typeof yesNo [keyof typeof yesNo]
  ) => {
    const returnObject: {
      note: string | null,
      lotteryNote: string | null
    } = {
      note: null,
      lotteryNote: null
    }

    // 利用目的別設定で利用目的が未選択の場合
    if ( commonFlag === yesNo.no && purposeOfUse === undefined ) return returnObject;

    if ( !purposeOfUse ) {
      // 共通設定（利用目的利用・未利用何れも含む）
      returnObject.note = facility.detailSetting[0].note;
      if ( acceptLottery && facility.lotteryNotes ) {
        returnObject.lotteryNote = facility.lotteryNotes[0].note;
      }
    } else {
      // 利用目的が選択されている場合、または利用・抽選の何れかのみが利用目的別の設定の場合
      returnObject.note = facility.detailSetting.find((setting) => setting.useReasonCode === purposeOfUse)?.note ?? facility.detailSetting[0].note;
      if ( acceptLottery && facility.lotteryNotes ) {
        if ( facility.lotteryAcceptType === lotteryAcceptTypeOption.common ) {
          returnObject.lotteryNote = facility.lotteryNotes[0].note;
        } else {
          returnObject.lotteryNote = facility.lotteryNotes.find((setting) => setting.useReasonCode === purposeOfUse)?.note?? null;
        }
      }
    }

    return returnObject;
  }, [])

  // 初期化
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 })

    dispatch(
      showLoading(
        errorHandle(async () => {
          const backedFromNextPage = locationState && locationState.isKeep && reserveEntry

          let activeDate: Date = sysCtrl.systemDatetime? new Date(sysCtrl.systemDatetime) : getNowTrimedTime()
          let selectedChildIds: string[] = []
          let facilityId: string | null = null
          let purposeOfUse: string | null | undefined = undefined
          let useReasonDetailCategory: string | null = null
          let useReasonDetailDescription: string | null = null
          let useReasonDetailIrregular: string | null = null
          let useReasonDetailEmg: string | null = null
          let useReasonDetailRefresh: string | null = null;
          let isCertificateUseReasonViewFlags: IsCertificateUseReasonViewFlags = {irregularFlag: true, emgFlag: true, refreshFlag: true}
          let children: Child[] = []
          let usageDesiredDatetimes: DesiredDatetime[] = []
          let reservationNo: string | null = null
          let prevReservation: Reservation | undefined;
          let diagonosis: {
            first: string | null,
            second: string | null
          } = { first: null, second: null }
          let inputDiagonosis: DiagonosisType | undefined = {
            first: {
              select: null,
              other: null
            },
            second: {
              select: null,
              other: null
            }
          }

          // 新規登録 OR 利用予約変更
          let updateFlag = false
          if (isInsertParams(urlParams)) {
            updateFlag = false
            if (locationState && backedFromNextPage) {
              // ページ戻りの場合
              activeDate = new Date(locationState.activeDate)
              selectedChildIds = reserveEntry.childInfoList.map((childInfo) => childInfo.childId)
              facilityId = reserveEntry.facility.facilityId
              purposeOfUse = reserveEntry.useReasonCategory
              useReasonDetailCategory = reserveEntry.useReasonDetailCategory;
              useReasonDetailDescription = reserveEntry.useReasonDetailDescription
              isCertificateUseReasonViewFlags = reserveEntry.isCertificateUseReasonViewFlags
              children = locationState.children
              usageDesiredDatetimes = reserveEntry.usageDatetimes.map((v) => ({
                value: {
                  baseDateOfTime: new Date(v.usageDate),
                  range: { from: new Date(v.useFromDatetime), to: new Date(v.useToDatetime)},
                  status: v.status,
                },
              }))
              diagonosis = {
                first: reserveEntry.childInfoList[0].diagonosis?.first.select ?? null,
                second: reserveEntry.childInfoList[0].diagonosis?.second.select ?? null
              }
              inputDiagonosis = reserveEntry.childInfoList[0].diagonosis
            } else {
              selectedChildIds = [urlParams.childId]
              facilityId = urlParams.facilityId
              children = locationState?.children ?? []
            }
            selectedChildIds.forEach((childId) => {
              attachAccessData({
                accessData: [
                  { 
                    userIdRegFlag: yesNo.yes, 
                    childId: childId
                  }
                ]
              })
            }) 
          } else {
            updateFlag = true
            reservationNo = urlParams.reservationNo
            prevReservation = await getReservation(reservationNo)
            let usageDate: number;
            if (locationState && backedFromNextPage) {
              // ページ戻りの場合
              activeDate = new Date(locationState.activeDate)
              selectedChildIds = reserveEntry.childInfoList.map((childInfo) => childInfo.childId)
              facilityId = reserveEntry.facility.facilityId
              purposeOfUse = reserveEntry.useReasonCategory
              useReasonDetailCategory = reserveEntry.useReasonDetailCategory;
              useReasonDetailDescription = reserveEntry.useReasonDetailDescription
              isCertificateUseReasonViewFlags = reserveEntry.isCertificateUseReasonViewFlags
              children = locationState.children
              usageDesiredDatetimes = [{
                value: {
                  baseDateOfTime: new Date(reserveEntry.usageDatetimes[0].usageDate),
                  range: { from: new Date(reserveEntry.usageDatetimes[0].useFromDatetime), to: new Date(reserveEntry.usageDatetimes[0].useToDatetime)},
                  status: reserveEntry.usageDatetimes[0].status,
                }
              }]
              usageDate = reserveEntry.usageDatetimes[0].usageDate
              diagonosis = {
                first: reserveEntry.childInfoList[0].diagonosis?.first.select ?? null,
                second: reserveEntry.childInfoList[0].diagonosis?.second.select ?? null
              }
              inputDiagonosis = reserveEntry.childInfoList[0].diagonosis
            } else {
              activeDate = new Date(prevReservation.usageDate)
              selectedChildIds = prevReservation.childInfoList.map((childInfo) => childInfo.childId)
              children = [{ value: selectedChildIds[0], label: prevReservation.childInfoList[0].childInfo.name }]
              facilityId = prevReservation.facilityId
              purposeOfUse = prevReservation.useReasonCategory
              useReasonDetailCategory = prevReservation.useReasonDetailCategory;
              useReasonDetailDescription = prevReservation.useReasonDetailDescription
              usageDesiredDatetimes = [{
                value: {
                  baseDateOfTime: activeDate,
                  range: { from: new Date(prevReservation.useFromDatetime), to: new Date(prevReservation.useToDatetime)},
                  status: prevReservation.status,
                },
              }]
              usageDate = prevReservation.usageDate
              diagonosis = {
                  first: prevReservation.childInfoList[0].diagonosis?.first.select ?? null,
                  second: prevReservation.childInfoList[0].diagonosis?.second.select ?? null
                }
              inputDiagonosis = prevReservation.childInfoList[0].diagonosis
            }
            attachAccessData({
              accessData: [
                {
                  userIdRegFlag: yesNo.yes,
                  childId: selectedChildIds[0],
                  usageDate: toApiYmd(usageDate),
                  reservationNo: reservationNo,
                }
              ],
            })
          }

          if (!facilityId) {
            notifyDateFetchErrorMessage()
            return
          }

          const [facility, childrenResponse, useReason, diagonosisDecodeMst] = await Promise.all([
            getFacility(facilityId, activeDate),
            executeGetChildren({ 
              facilityId,
              getLatestInterviewedFlag: Flag.OFF
            }),
            getUseReason(),
            getDiagonosisDecodeMstValue()
          ]);

          if (facility.detailSetting.length === 0 || facility.detailSetting[0].useReasonCode === null) {
            notifyDateFetchErrorMessage()
            return
          }

          const childrenData = updateFlag ? childrenResponse.result.filter(({ childId }) => childId === children[0].value) : childrenResponse.result
          
          if (childrenData.length) {
            children = childrenData.map((child) => ({ value: child.childId, label: child.name }));
            if (!backedFromNextPage) {
              if (facility.certificateFlag === yesNo.yes) {
                if(facility.certificateControlAtypical === yesNo.yes && childrenData.every((child) => child.useFlagAtypical === yesNo.no)) {
                  isCertificateUseReasonViewFlags.irregularFlag = false;
                }
                if(facility.certificateControlEmergency === yesNo.yes && childrenData.every((child) => child.useFlagEmergency === yesNo.no)) {
                  isCertificateUseReasonViewFlags.emgFlag = false;
                }
                if(facility.certificateControlRefresh === yesNo.yes && childrenData.every((child) => child.useFlagRefresh === yesNo.no)) {
                  isCertificateUseReasonViewFlags.refreshFlag = false;
                }
              } 
            }
          }

          const useReasonDetailDescriptionsIrregular: DetailDescription[] = Array(useReason.detailsIrregular.length).fill(null).map(() => ({ value: null }));
          const useReasonDetailDescriptionsEmg: DetailDescription[] = Array(useReason.detailsEmg.length).fill(null).map(() => ({ value: null }));
          const useReasonDetailDescriptionsRefresh: DetailDescription[] = Array(useReason.detailsRefresh.length).fill(null).map(() => ({ value: null }));

          // 抽選予約を受け付けているか
          const acceptLottery =  (
            sysCtrl.lotteryDisplayFlag === yesNo.yes 
            && sysCtrl.lotteryFlag === yesNo.yes 
            && facility.lotteryFlag === yesNo.yes 
          )

          // 各利用目的の利用可否
          const useReasonOptions: UseReasonOptions = {
            irregular: 
              facility.irregularDisplayType === useReasonDisplayTypes.usedInBoth
              && facility.useReasonUsageFlagIrregular === yesNo.yes
              && isCertificateUseReasonViewFlags.irregularFlag,
            emergency: 
              facility.emergencyDisplayType === useReasonDisplayTypes.usedInBoth
              && facility.useReasonUsageFlagEmg === yesNo.yes
              && isCertificateUseReasonViewFlags.emgFlag,
            refresh:
              facility.refreshDisplayType === useReasonDisplayTypes.usedInBoth
              && facility.useReasonUsageFlagRefresh === yesNo.yes
              && isCertificateUseReasonViewFlags.refreshFlag,
          };

          // 共通OR利用目的別設定かの判断
          let commonFlag: CommonFlag = yesNo.no;
          if (
            facility.detailSetting.length === 1 
            && facility.detailSetting[0].useReasonCode === useReasonCodeValue.common 
            && ( !acceptLottery || facility.lotteryAcceptType === lotteryAcceptTypeOption.common)
          ) {  
            commonFlag = yesNo.yes;
            if (Object.values(useReasonOptions).every((value)=> !value)) {
              // 全ての利用目的が使用されない場合
              purposeOfUse = null;
            }
          }
          
          // カレンダー説明文
          const usageGuideStr = getUsageGuidStr(
            facility.immediatelyReservationFlag,
            facility.cancelWaitAcceptFlag,
            facility.postponeCancelWaitAcceptFlag,
            acceptLottery
          );

          switch (purposeOfUse) {
            case purposeOfUseValue.irregular:
              useReasonDetailIrregular = useReasonDetailCategory
              if (useReasonDetailIrregular) {
                const index = Number(useReasonDetailIrregular) - 1
                useReasonDetailDescriptionsIrregular[index].value = useReasonDetailDescription
              }
              
              break;
            case purposeOfUseValue.emergency:
              useReasonDetailEmg = useReasonDetailCategory
              if (useReasonDetailEmg) {
                const index = Number(useReasonDetailEmg) - 1
                useReasonDetailDescriptionsEmg[index].value = useReasonDetailDescription
              }
              break;
            case purposeOfUseValue.refresh:
              useReasonDetailRefresh = useReasonDetailCategory
              if (useReasonDetailRefresh) {
                const index = Number(useReasonDetailRefresh) - 1
                useReasonDetailDescriptionsRefresh[index].value = useReasonDetailDescription
              }
              break;
            default:
              break;
          }
          appendUseReasonDetailDescriptionsIrregular(useReasonDetailDescriptionsIrregular);
          appendUseReasonDetailDescriptionsEmg(useReasonDetailDescriptionsEmg);
          appendUseReasonDetailDescriptionsRefresh(useReasonDetailDescriptionsRefresh);

          const canGetAvaiabilitiesForWeek = 
            ((!facility.isolationFlag && commonFlag === yesNo.yes && purposeOfUse !== undefined)
            ||  
            (facility.isolationFlag && (diagonosis.first || diagonosis.second) && (commonFlag === yesNo.yes || purposeOfUse !== undefined)))?? false

          const [availabilities, initialValidationAvailabilities] = await Promise.all([
            canGetAvaiabilitiesForWeek ? getAvaiabilitiesForWeek(
              facilityId,
              selectedChildIds,
              activeDate,
              purposeOfUse,
              reservationNo,
              diagonosis
            ) : [],
            purposeOfUse !== undefined ? getAvailabilitiesByDays(
              facilityId,
              selectedChildIds,
              getInitialSelectedUsageApiYmds(locationState?.isKeep, reserveEntry),
              reservationNo,
              diagonosis,
              purposeOfUse,
            ): []
          ]);

          const { note, lotteryNote } = getNotes(facility, acceptLottery, purposeOfUse, commonFlag);

          formMethods.reset({
            usageDesiredDatetimes,
            purposeOfUse,
            useReasonDetailIrregular,
            useReasonDetailEmg,
            useReasonDetailRefresh,
            useReasonDetailDescriptionsIrregular,
            useReasonDetailDescriptionsEmg,
            useReasonDetailDescriptionsRefresh,
            selectedChildIds,
            diagonosis: inputDiagonosis
          })
          setState({
            prevReservation,
            activeDate,
            selectedChildIds,
            children,
            facilityId,
            facility,
            purposeOfUse,
            useReasonDetailCategory,
            useReasonDetailDescription,
            commonFlag,
            useReasonOptions,
            isCertificateUseReasonViewFlags,
            usageGuideStr,
            useReason,
            availabilities,
            initialValidationAvailabilities,
            diagonosisDecodeMst,
            isolationFlag: facility.isolationFlag,
            diagonosis,
            acceptLottery,
            note,
            lotteryNote
          })
        })
      )
    )
  }, [])
  
  // コンテンツが表示された後に一番上にスクロールする
  useEffect(() => {
    if (!state || state.availabilities.length === 0 || initialized.current) return
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
    initialized.current = true;
  }, [state])
  
  // 条件が変わったときに空き状況確認する
  useEffect(() => {
    if (!state) return
    if (state.commonFlag === null) {
      return
    }

    if (state.selectedChildIds.length === 0) {
      setState((prev) => (
        { 
          ...prev as PageState, 
          availabilities: [], 
        })
      );
      return
    }
      
    dispatch(
      showLoading({
        process: errorHandle(async () => {
          if (state.commonFlag === yesNo.no && state.purposeOfUse === undefined) return
          if (state.isolationFlag && Object.values(state.diagonosis).every(v => v === null)) return

          const [
            availabilities,
            facility,
          ] = await Promise.all([
            getAvaiabilitiesForWeek(
              state.facilityId,
              state.selectedChildIds,
              state.activeDate,
              state.purposeOfUse,
              getAvailabilitiesReservationNo(state.prevReservation),
              state.diagonosis,
            ),
            getFacility(state.facilityId, state.activeDate),
          ])

          if (availabilities) {
            setState((prev) => (
              { 
                ...prev as PageState, 
                availabilities,
                facility,
              })
            );
          }else {
            // 異常処理
            notifyDateFetchErrorMessage()
          }
        }),
        isHiddenMain: false,
      })
    );
  }, [
      state?.selectedChildIds,
      state?.activeDate,
      state?.purposeOfUse,
      state?.diagonosis
    ]
  );

  // お子さま変更
  const changeSelectedChildIds = useCallback((childId: string, checked: boolean) => {
    if (!state || !childId) return
    addOperationLog({ operationId: OperationId.OP_00000028 })

    let updatedselectedChildIds = [...state.selectedChildIds]
    if( checked ) {
      if ( !updatedselectedChildIds.includes( childId ) ) {
        updatedselectedChildIds.push(childId);
      }
    }else {
      updatedselectedChildIds = updatedselectedChildIds.filter( ( id ) => id !== childId )
    }
    
    setState((prev) => ({
      ...prev as PageState,
      selectedChildIds: updatedselectedChildIds
    }))
  }, [state])

  // お子さま変更
  const changeDropDownSelectedChildId = useCallback((event: SelectChangeEvent<string>) => {
    if (!state) return
    const childId = event.target.value;
    if ( !childId ) return;
    
    setState((prev) => ({
      ...prev as PageState,
      selectedChildIds: [childId]
    }))
  }, [state])

  // 日付変更
  const changeDate = useCallback((date: Date | null): void => {
    if (!state) return
    addOperationLog({ operationId: OperationId.OP_00000029 })
    
    if (!isValidDate(date)) {
      notifyDateFetchErrorMessage();
      return;
    }

    setState((prev) => ({
      ...prev as PageState,
      activeDate: date
    }));

  }, [state])
  
  // 利用目的変更
  const onChangeUseReason = useCallback((newPurpose: string): void => {
    if (
      state?.commonFlag === undefined ||
      state?.facility === undefined ||
      state?.acceptLottery === undefined
    ) return
    formMethods.reset({
      ...formMethods.getValues(),
      purposeOfUse: newPurpose,
    })

    if (state.commonFlag === yesNo.yes) return

    setState((prev) => ({
      ...prev as PageState,
      purposeOfUse: newPurpose,
      ...getNotes(state.facility, state.acceptLottery, newPurpose, state.commonFlag)
    }));

  }, [
    state?.commonFlag,
    state?.facility,
    state?.acceptLottery,
  ])

  // 病気変更
  const changeDropDownSelectedDiagonosis = useCallback(
    (field: 'first' | 'second') => (event: SelectChangeEvent<string>) => {
      if (!state) return;

    const diagonosisValue = event.target.value;
    if (!diagonosisValue) return;
  
      setState((prev) => ({
        ...prev as PageState,
        diagonosis: {
          ...(prev?.diagonosis ?? { first: null, second: null }),
          [field]: diagonosisValue,
        },
      }))
    },
    [state]
  )

  const handleChangeFirst = (event: SelectChangeEvent<string>) => {
    changeDropDownSelectedDiagonosis('first')(event);
  };
  
  const handleChangeSecond = (event: SelectChangeEvent<string>) => {
    changeDropDownSelectedDiagonosis('second')(event);
  };
    
  /**
   * 妥当性検査
   * @param data 入力値
   * @param facilityId 施設ID
   * @returns エラーが存在する場合true
   */
  const validate = useCallback(async (data: Inputs, facilityId: string) => {
    const response: {isError: boolean; showMessages: ShowMessage[]} = {
      isError: false,
      showMessages: []
    }
    if (!state) return response;
    
    const facilityMap = await getFacilityMapByUsageDateElapsed(
      facilityId,
      new Set(data.usageDesiredDatetimes.map((v) => v.value.baseDateOfTime.getTime()))
    )
    
    const areAnyCheckboxesSelected = formMethods.getValues(`selectedChildIds`).every(item => item === '')
    if (areAnyCheckboxesSelected) {
      formMethods.setError(`selectedChildIds.0`, {
        message: translate(
          'facilityReservationSelection.error.noSelectionChild'
        ),
      })
      response.isError = true
    }

    // 対象日を取得する
    const entriesArray = Array.from(data.usageDesiredDatetimes.entries())

    for (const [idx, { value: { baseDateOfTime, range } }] of entriesArray) {
      const facility = facilityMap.get(baseDateOfTime.getTime())
      const minReservationMinute = facility?.minReservationMinute
      const reservableUnitPatternMinute = facility && reservableUnitPatterMinutes[facility?.reservableUnitPattern]
      if (minReservationMinute != null) {
        // 最低予約時間が設定されていれる場合
        const minute = differenceInMinutes(range.to, range.from)
        if (minute < minReservationMinute) {
          formMethods.setError(`usageDesiredDatetimes.${idx}.value`, {
            message: translate(
              'facilityReservationSelection.error.minReservationMinute',
              fromNumber(minReservationMinute)
            ),
          })
          response.isError = true
        }
      }
      if (reservableUnitPatternMinute != null) {
        // 予約可能単位が設定されていれる場合
        const minute = differenceInMinutes(range.to, range.from)
        if (minute % reservableUnitPatternMinute !== 0) {
          formMethods.setError(`usageDesiredDatetimes.${idx}.value`, {
            message: translate(
              'facilityReservationSelection.error.reservableUnitPattern',
              fromNumber(reservableUnitPatternMinute)
            ),
          })
          response.isError = true
        }
      }
      // 重複チェック
      const prevReservations: GetReservationsDto[] = (await executeGetReservations({ childId: state.selectedChildIds[0] ?? null, usageData: formatHyphenYmd(baseDateOfTime) })).result
      const duplicateConfirmControlFlag = sysCtrl?.duplicateConfirmControlFlag
      const duplicateUnsettledControlFlag = sysCtrl?.duplicateUnsettledControlFlag
      const duplicateWaitControlFlag = sysCtrl?.duplicateWaitControlFlag
      const crossBusinessFlg = sysCtrl?.crossBusinessDuplicationCheckFlag
      
      if (prevReservations.length > 0) {
        for (const reservation of prevReservations) {
          if (reservation.reservationNo === state?.prevReservation?.reservationNo) {
            continue;
          }
          
          if (crossBusinessFlg === yesNo.no && facility?.projectName !== reservation?.projectId) {
            continue;
          }
          
          if ( reservation.status === reservationStatus.canceled 
            || reservation.status === reservationStatus.canceledWait
            || reservation.status === reservationStatus.rejected
            || reservation.status === reservationStatus.unused ) {
            continue;
          }
            
          if ( new Date(reservation.useFromDatetime) >=　range.to || new Date(reservation.useToDatetime) <= range.from ) {
            continue;
          }
          
          const duplicateCtrlStatus = [];
          if ( duplicateUnsettledControlFlag === yesNo.yes ) {
            duplicateCtrlStatus.push(translate('facilityReservationSelection.error.duplicateReservationStatus.notFixed'))
          }
          if ( duplicateWaitControlFlag === yesNo.yes ) {
            duplicateCtrlStatus.push(translate('facilityReservationSelection.error.duplicateReservationStatus.wait'))
          }
          if ( duplicateConfirmControlFlag === yesNo.yes ) {
            duplicateCtrlStatus.push(translate('facilityReservationSelection.error.duplicateReservationStatus.fixed'))
          }
          const strDuplicateCtrlStatus = duplicateCtrlStatus.join('・')

          let isError = false
          let message = ''
          
          switch( reservation.status ) {
            case reservationStatus.notFixed:
              if ( duplicateUnsettledControlFlag === yesNo.yes ) {
                isError = true
                message += strDuplicateCtrlStatus
              }
              break
            case reservationStatus.fixed:
              if ( duplicateConfirmControlFlag === yesNo.yes ) {
                isError = true
                message += strDuplicateCtrlStatus
              }
              break
            case reservationStatus.wait:
              if ( duplicateWaitControlFlag === yesNo.yes ) {
                isError = true
                message += strDuplicateCtrlStatus
              }
              break
            case reservationStatus.used:
              isError = true
              message += translate('facilityReservationSelection.error.duplicateReservationStatus.used')
              break
            default:
              break
          }
          
          if ( isError ) {
            formMethods.setError(`usageDesiredDatetimes.${idx}.value`, {
              message: message + translate('facilityReservationSelection.error.duplicateReservationBase') + formatYmdHm(new Date(reservation.useFromDatetime)) + ' ~ ' + formatYmdHm(new Date(reservation.useToDatetime))
            })
            response.isError = true
            break
          }
        }
      }
    }

    return response
  }, [state])

  const onSubmit = useCallback((data: Inputs) => {
    if (!state) return
    addOperationLog({ operationId: OperationId.OP_00000030 })

    const facilityId = castNonNullable(state.facilityId)
    
    const formatDateToYYYYMMDD = (dateTime: Date) => {
      // 年、月、日を取得
      const year = dateTime.getFullYear();
      const month = String(dateTime.getMonth() + 1).padStart(2, '0');
      const day = String(dateTime.getDate()).padStart(2, '0');
      
      // yyyy-mm-dd形式に変換
      const formattedDate = `${year}-${month}-${day}`
      return formattedDate;
    }
    dispatch(
      showLoading({
        process: errorHandle(async () => {
          // 妥当性検査
          const isValidateError = await validate(data, facilityId)
          if (isValidateError.isError) {
            return
          }
          
          let useReasonDetailCategory: string | null = null
          let useReasonDetailDescription: string | null = null
    
          switch (data.purposeOfUse) {
            case purposeOfUseValue.irregular:
              useReasonDetailCategory = data.useReasonDetailIrregular
              if (useReasonDetailCategory){
                const indexOfDescription = Number(useReasonDetailCategory) - 1
                useReasonDetailDescription = data.useReasonDetailDescriptionsIrregular[indexOfDescription].value
              }
              break
            case purposeOfUseValue.emergency:
              useReasonDetailCategory = data.useReasonDetailEmg
              if (useReasonDetailCategory){
                const indexOfDescription = Number(useReasonDetailCategory) - 1
                useReasonDetailDescription = data.useReasonDetailDescriptionsEmg[indexOfDescription].value
              }
              break
            case purposeOfUseValue.refresh:
              useReasonDetailCategory = data.useReasonDetailRefresh
              if (useReasonDetailCategory){
                const indexOfDescription = Number(useReasonDetailCategory) - 1
                useReasonDetailDescription = data.useReasonDetailDescriptionsRefresh[indexOfDescription].value
              }
              break
          }

          if (!state.facility || !state.facilityId) {
            return
          }

          if (data.usageDesiredDatetimes[0].value.status === availabilityStatus.lotteryReservationAvailable) {
            // 抽選予約の場合             
            dispatch(
              setLotteryReserve({
                usageDatetimes: data.usageDesiredDatetimes
                  .map((v) => ({
                    usageDate: v.value.baseDateOfTime.getTime(),
                    useFromDatetime: v.value.range.from.getTime(),
                    useToDatetime: v.value.range.to.getTime(),
                    usageBaseDate: formatDateToYYYYMMDD(v.value.baseDateOfTime),
                    status: v.value.status,
                  }))
                  .sort((a, b) => a.useFromDatetime - b.useFromDatetime),
                facility: {
                  facilityId: state.facilityId,
                  ...state.facility
                },
                childInfoList: createChildInfoListForLottery(state.selectedChildIds, undefined),
                isCertificateUseReasonViewFlags: state.isCertificateUseReasonViewFlags,
                useReasonCategory: data.purposeOfUse,
                useReasonDetailCategory,
                useReasonDetailDescription,
                isUseReasonCategoryHidden: isUseReasonCategoryHidden(data.purposeOfUse, state.facility),
              })
            )
            dispatch(clearFacilityReserve())

            // 戻るで表示した際に取得・入力済み情報から復元を試みる為に履歴に保管
            history.replace({
              ...history.location,
              state: {
                isKeep: true,
                activeDate: state.activeDate.getTime(),
                children: state.children
              },
            })

            history.push(facilityLotteryReservationFormUrl.url())
          } else {
            // 抽選予約以外の場合
            dispatch(
              setFacilityReserve({
                facility: {
                  facilityId: state.facilityId,
                  ...state.facility
                },
                reservationNo: state.prevReservation?.reservationNo,
                // 確認しやすくするために利用開始日でソートしておく
                showMessages: isValidateError.showMessages
                .sort((a, b) => a.Date.getTime() - b.Date.getTime())
                .map(({ Date, ...rest}) => ({
                  ...rest,
                  Date: Date.toDateString()
                })),
                useReasonCategory: data.purposeOfUse,
                useReasonDetailCategory,
                useReasonDetailDescription,
                isUseReasonCategoryHidden: isUseReasonCategoryHidden(data.purposeOfUse, state.facility),
                
                usageDatetimes: data.usageDesiredDatetimes
                .map((v) => ({
                  usageDate: v.value.baseDateOfTime.getTime(),
                  useFromDatetime: v.value.range.from.getTime(),
                  useToDatetime: v.value.range.to.getTime(),
                  usageBaseDate: formatDateToYYYYMMDD(v.value.baseDateOfTime),
                  status: v.value.status,
                }))
                .sort((a, b) => a.useFromDatetime - b.useFromDatetime),
                
                childInfoList: createChildInfoList(state.selectedChildIds, state.diagonosis, data.diagonosis, state.prevReservation),
                ...(state.prevReservation && (
                  {
                    commonInput: {
                      lunchFlag: state.prevReservation.lunchFlag,
                      snackFlag: state.prevReservation.snackFlag,
                      postponeCancelWaitFlag: state.prevReservation.postponeCancelWaitFlag,
                      goingTimes: [
                        ... ( 
                          isUsageDatetimeUpdated(
                            state.prevReservation.useFromDatetime,
                            state.prevReservation.useToDatetime,
                            data.usageDesiredDatetimes[0].value.range.from.getTime(),
                            data.usageDesiredDatetimes[0].value.range.to.getTime()
                          )
                          ? []
                          : [
                              state.prevReservation.goingTime
                            ]
                        )
                      ],
                      preferenceProvisionCertificateFlag: state.prevReservation.preferenceProvisionCertificateFlag,
                      formUseReason: state.prevReservation.formUseReason,
                      citizenNote: state.prevReservation.citizenNote,
                    },
                  }
                )),
                isCertificateUseReasonViewFlags: state.isCertificateUseReasonViewFlags,
                isolationFlag: state.isolationFlag
              })
            )
            dispatch(clearLotteryReserve())

            if (state.prevReservation) {
              dispatch(setReserveModifyUpdateDatetime(state.prevReservation.updateDatetime))
            }

            /** 戻るで表示した際に取得・入力済み情報から復元を試みる為に履歴に保管 */
            history.replace({
              ...history.location,
              state: {
                isKeep: true,
                activeDate: state.activeDate.getTime(),
                children: state.children,
              },
            })

            history.push(facilityReservationFormUrl.url())
          }
        }),
        isHiddenMain: false,
      })
    )
  }, 
  [
    reserveEntry,
    state,
    validate,
    addOperationLog
  ])

  return {
    state,
    previousRange,
    formMethods,
    onSubmit,
    changeSelectedChildIds,
    changeDropDownSelectedChildId,
    changeDate,
    useReason: state?.useReason,
    purposeOfUse,
    useReasonDetailIrregular,
    useReasonDetailEmg,
    useReasonDetailRefresh,
    onChangeUseReason,
    handleChangeFirst,
    handleChangeSecond,
    multiChildReservationAvailabilityFlag: sysCtrl?.multiChildReservationAvailabilityFlag,
    isShowFirstOTher: state?.diagonosisDecodeMst.find((c) => c.value === state?.diagonosis.first)?.label.includes("その他") ?? false,
    isShowSecondOTher: state?.diagonosisDecodeMst.find((c) => c.value === state?.diagonosis.second)?.label.includes("その他") ?? false
  }
}

const createChildInfoList = (selectedChildrenIds: string[], diagonosis: {first: string | null, second: string | null}, inputDiagonosis: Inputs['diagonosis'], reservationData?: Reservation): ChildReservationInfoType[] => {
  if ( reservationData ) return [{
    ...reservationData.childInfoList[0],
    ... (
      (diagonosis.first || diagonosis.second) && {
        diagonosis: {
          first: {
            select: diagonosis.first,
            other: inputDiagonosis?.first.other ?? null
          },
          second: {
            select: diagonosis.second,
            other: inputDiagonosis?.second.other ?? null
          }
        }        
      }
    )
  }];
  
  const childInfoList: ChildReservationInfoType[] = []
  for (const selectedChildId of selectedChildrenIds) {
    childInfoList.push({
      childId: selectedChildId,
      diagonosis: {
        first: {
          select: diagonosis.first,
          other: inputDiagonosis?.first.other ?? null
        },
        second: {
          select: diagonosis.second,
          other: inputDiagonosis?.second.other ?? null
        }
      },
      febrileSeizures: null,
      medicine: {
        rx: null,
        otc: null,
      },
      foodAllergyFlag: null,
    })
  }
  return childInfoList
}

const createChildInfoListForLottery = (selectedChildrenIds: string[], reservationData?: LotteryReservation): ChildLotteryReservationInfoType[] => {
  if ( reservationData ) return reservationData.childInfoList;
  
  const childInfoList: ChildLotteryReservationInfoType[] = []
  for (const selectedChildId of selectedChildrenIds) {
    childInfoList.push({
      childId: selectedChildId,
      febrileSeizures: null,
      medicine: {
        rx: null,
        otc: null,
      },
      foodAllergyFlag: null,
    })
  }
  return childInfoList
}

const getFacilityMapByUsageDateElapsed = async (facilityId: string, usageDateElapsedSet: Set<ElapsedMillisecond>) => {
  const facilities = await promiseAllConcurrency(
    Array.from(usageDateElapsedSet).map((usageDateElapsed) => async () => {
      const facility = await getFacility(facilityId, new Date(usageDateElapsed))
      return [usageDateElapsed, facility] as const
    }),
    MAX_WORKER_COUNT
  )
  return new Map(facilities)
}

/**
 * 空き状況取得用の受付No取得
 * キャンセル待ち予約の変更時は受付Noを指定して空き状況を取得する為
 * @param reservation
 * @returns
 */
const getAvailabilitiesReservationNo = (reservation: Reservation | undefined) =>
  reservation?.status === reservationStatus.wait ? reservation.reservationNo : null

/**
 * 初期選択の年月日を取得する
 *
 * @param isKeep 戻るで初期表示してreduxから状態を復元する際はtrue
 * @param reserveEntry 保存された状態
 * @returns 初期選択の利用日時から取得した年月日(yyyy-MM-dd)配列。※重複除去
 */
const getInitialSelectedUsageApiYmds = (isKeep?: boolean, reserveEntry?: FacilityReserveEntry | LotteryReserveEntry) => {
  return isKeep && reserveEntry
    ? Array.from(new Set(reserveEntry.usageDatetimes.map((v) => toApiYmd(new Date(v.usageDate)))))
    : []
}

/**
 * 指定日分の空き状況を取得する
 *
 * @param facilityId 施設ID
 * @param childrenIds
 * @param targetApiYmds 取得対象年月日(yyyy-MM-ddの配列)
 * @param reservationNo キャンセル待ち予約変更時は対象予約No。それ以外はnull
 * @param useReasonCode 予約受付設定コード 0:共通 1:非定型 2:緊急 3:リフレッシュ
 * @returns 空き状況
 */
const getAvailabilitiesByDays = async (
  facilityId: string,
  childrenIds: string[],
  targetApiYmds: string[],
  reservationNo: string | null,
  diagonosis: {
    first?: string | null,
    second?: string | null
  },
  useReasonCode: string | null,
) => {
  const selectedChildrenIds = childrenIds.filter(item => item !== '')
  const concatedSelectedChildrenIds = selectedChildrenIds.join(',')
  let result: GetFacilityReservationsDaysDto[] = []
  if (targetApiYmds.length) {
    const resp = await executeGetFacilityReservationsDays(facilityId, {
      childId: concatedSelectedChildrenIds,
      targetDates: targetApiYmds,
      reservationNo,
      useReasonCode: useReasonCode?? purposeOfUseValue.common,
      diagonosis
    })
    result = resp.result
  }
  return result
}

const getAvaiabilitiesForWeek = async (
  facilityId: string,
  selectedChildIds: string[],
  targetDate: Date,
  useReasonCode: string | null | undefined,
  lotteryReservationNo: string | null,
  diagonosis: {
    first: string | null,
    second: string | null
  }
) => {
  const concatedSelectedChildrenIds = selectedChildIds.join(',')
  const response = await executeGetFacilityReservationsWeekCitizen(facilityId, {
    childId: concatedSelectedChildrenIds,
    targetDate: toApiYmd(targetDate),
    useReasonCode: useReasonCode ?? purposeOfUseValue.common,
    reservationNo: lotteryReservationNo,
    diagonosis
  });
  return response.result;
}

/**
 * 予約変更を行っている予約の利用日時に更新がされているか確認する
 * @param prevUseFromDatetime 
 * @param prevUseToDatetime 
 * @param currUseFromDatetime 
 * @param currUseToDatetime 
 * @returns 
 */
const isUsageDatetimeUpdated = (
  prevUseFromDatetime: number,
  prevUseToDatetime: number,
  currUseFromDatetime: number,
  currUseToDatetime: number
) => {
  return (
    prevUseFromDatetime !== currUseFromDatetime
    || prevUseToDatetime !== currUseToDatetime
  );
}
